/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { forwardRef, type JSX } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericPercentages,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import type { ButtonProps } from "../../_core/Button/Button.emotion";
import { Button } from "../../_core/Button/Button.emotion";
import { CheckmarkIcon } from "../../icons/Icon/GeneralIcons";
import type { DropdownMenuItemProps } from "../DropdownMenuItem/DropdownMenuItem.emotion";

const baseStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  ":hover": {
    backgroundColor: colors.transparent,
    color: theme.semanticColors.primary,
    [cssMediaQueryMinWidthTablet]: {
      backgroundColor: theme.semanticColors.onOverlayLow,
    },
  },
  ":active": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.neutralsMedHigh,
  },
  borderRadius: radius.m,
  color: theme.semanticColors.primary,
  cursor: "pointer",
  display: "inline-flex",
  height: "auto",
  justifyContent: "space-between",
  maxWidth: spacing.full,
  minHeight: numericPixels[56],
  outline: "none",
  overflow: "hidden",
  padding: spacing.m,
  textAlign: "initial",
  whiteSpace: "revert",
  width: numericPercentages[100],
});
const selectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
});

export type ModalItemProps = Omit<ButtonProps, "size" | "color" | "variant"> & {
  selected?: boolean;
  textValue?: DropdownMenuItemProps["textValue"];
  hideCheckOnSelected?: boolean;
};

export const ModalItem = forwardRef<HTMLButtonElement, ModalItemProps>(
  (
    {
      children,
      onClick,
      selected = false,
      onSelect,
      disabled,
      textValue,
      hideCheckOnSelected,
      ...props
    },
    ref,
  ) => {
    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        selected && !hideCheckOnSelected ? <CheckmarkIcon.SimpleOn /> : null,
      ],
      beforeChildrenInject: null,
      children: textValue ? <>{textValue}</> : (children as JSX.Element),
    });

    return (
      <Button
        ref={ref}
        onClick={(e) => {
          if (onSelect) {
            onSelect(e);
          }
          // This onClick is passed on from DialogClose and is used to close the modal when an option is selected
          if (onClick) {
            onClick(e);
          }
        }}
        disabled={disabled}
        variant={"transparentPrimary"}
        css={[baseStyle, selected && selectedStyle]}
        {...props}
      >
        {DeterminedChildren}
      </Button>
    );
  },
);

ModalItem.displayName = "ModalItem";
